<template>
  <div class="quality_history_line_chart">
    <div class="bar-chart-scroll">
      <div class="bar-chart-container">
        <canvas ref="canvas"></canvas>
      </div>
    </div>
  </div>
</template>

<script>
import { Line } from 'vue-chartjs';
import { RATING_COLORS } from '../helpers/qualityHistory';

const RATING_VALUES = {
  high: 3,
  medium: 2,
  low: 1,
  na: 0,
};

export default {
  extends: Line,
  name: 'QualityHistoryLineChart',
  props: {
    history: {
      type: Array,
      required: true,
    },
  },
  methods: {
    normalizeRating(rating) {
      return RATING_VALUES[rating] !== undefined ? rating : 'na';
    },
    ratingToValue(rating) {
      return RATING_VALUES[this.normalizeRating(rating)];
    },
    levelLabel(rating) {
      const key = this.normalizeRating(rating);
      const labelKeys = {
        high: 'INBOX_MGMT.QUALITY.CHART.LEVEL_HIGH',
        medium: 'INBOX_MGMT.QUALITY.CHART.LEVEL_MEDIUM',
        low: 'INBOX_MGMT.QUALITY.CHART.LEVEL_LOW',
        na: 'INBOX_MGMT.QUALITY.CHART.LEVEL_NA',
      };
      return this.$t(labelKeys[key]);
    },
    sortedHistory() {
      return [...this.history].sort(
        (a, b) => new Date(a.date) - new Date(b.date)
      );
    },
    formatDate(isoDate) {
      const [year, month, day] = isoDate.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    },
    updateChart() {
      const sorted = this.sortedHistory();
      if (sorted.length === 0) return;
      if (!this.$refs.canvas) return;

      const labels = sorted.map(entry => this.formatDate(entry.date));
      const data = sorted.map(entry => this.ratingToValue(entry.rating));
      const pointColors = sorted.map(
        entry => RATING_COLORS[this.normalizeRating(entry.rating)]
      );
      const yLabels = [
        this.$t('INBOX_MGMT.QUALITY.CHART.LEVEL_NA'),
        this.$t('INBOX_MGMT.QUALITY.CHART.LEVEL_LOW'),
        this.$t('INBOX_MGMT.QUALITY.CHART.LEVEL_MEDIUM'),
        this.$t('INBOX_MGMT.QUALITY.CHART.LEVEL_HIGH'),
      ];
      const maxYLabelLength = yLabels.reduce(
        (max, label) => Math.max(max, label.length),
        0
      );

      this.renderChart(
        {
          labels,
          datasets: [
            {
              label: this.$t('INBOX_MGMT.TABS.NUMBER_QUALITY'),
              data,
              borderColor: '#64748b',
              backgroundColor: 'transparent',
              pointBackgroundColor: pointColors,
              pointBorderColor: pointColors,
              pointRadius: 6,
              pointHoverRadius: 8,
              fill: false,
              lineTension: 0,
            },
          ],
        },
        {
          responsive: true,
          maintainAspectRatio: false,
          legend: {
            display: false,
          },
          tooltips: {
            callbacks: {
              label: tooltipItem => {
                const index = tooltipItem.index;
                const entry = sorted[index];
                return `${this.$t('INBOX_MGMT.TABS.NUMBER_QUALITY')}: ${this.levelLabel(
                  entry.rating
                )}`;
              },
            },
          },
          scales: {
            xAxes: [
              {
                gridLines: {
                  display: false,
                },
                ticks: {
                  autoSkip: false,
                  fontSize: 12,
                  fontStyle: 'bold',
                  padding: 20,
                  maxRotation: 45,
                  minRotation: 0,
                },
              },
            ],
            yAxes: [
              {
                ticks: {
                  min: 0,
                  max: 3,
                  stepSize: 1,
                  callback: value => {
                    const label = yLabels[value] || '';
                    return label ? label.padEnd(maxYLabelLength, ' ') : '';
                  },
                  fontSize: 12,
                  fontStyle: 'bold',
                  padding: 20,
                },
                gridLines: {
                  drawBorder: false,
                },
              },
            ],
          },
        }
      );
    },
  },
  watch: {
    history: {
      immediate: true,
      deep: true,
      handler() {
        if (this.history.length > 0) this.updateChart();
      },
    },
  },
  mounted() {
    this.updateChart();
  },
};
</script>

<style scoped>
.quality_history_line_chart {
  border-radius: 10px;
  box-shadow: 0px 0px 4px 0px rgba(0, 0, 0, 0.25);
  padding: 6rem;
  width: 100%;
}

.bar-chart-scroll {
  overflow-x: auto;
  width: 100%;
}

.bar-chart-container {
  position: relative;
  height: 300px;
  width: 100%;
  min-width: 600px;
}
</style>
